// src/services/authService.js
// Service d'authentification - Appels API backend

import api from './api';

export const normalizePhone = (phone) => {
  if (!phone) return '';
  let cleaned = phone.replace(/[\s\-()]/g, '');
  if (cleaned.startsWith('00221')) cleaned = '+221' + cleaned.slice(5);
  if (cleaned.startsWith('221') && !cleaned.startsWith('+')) cleaned = '+' + cleaned;
  if (cleaned.match(/^[37]\d{8}$/)) cleaned = '+221' + cleaned;
  return cleaned;
};

export const isValidSenegalPhone = (phone) => {
  const normalized = normalizePhone(phone);
  return /^\+221[37]\d{8}$/.test(normalized);
};

export const isValidSecretCode = (code) => /^\d{4,6}$/.test(String(code || '').trim());

export const formatPhoneDisplay = (phone) => {
  const normalized = normalizePhone(phone);
  if (!normalized.startsWith('+221')) return phone;
  const number = normalized.slice(4);
  return `+221 ${number.slice(0, 2)} ${number.slice(2, 5)} ${number.slice(5, 7)} ${number.slice(7, 9)}`;
};

function persistSession(responseData) {
  if (responseData.token) {
    localStorage.setItem('token', responseData.token);
    localStorage.setItem('user', JSON.stringify(responseData.user));
  }
}

const authService = {
  async getLoginHint(phone) {
    try {
      const normalizedPhone = normalizePhone(phone);
      const response = await api.get('/auth/login-hint', { params: { phone: normalizedPhone } });
      return {
        success: true,
        exists: response.data.exists,
        usesSecretCode: response.data.usesSecretCode,
      };
    } catch {
      return { success: false, usesSecretCode: false };
    }
  },

  async register(userData) {
    try {
      const { phone, secretCode, firstName, lastName, otpCode } = userData;
      const normalizedPhone = normalizePhone(phone);

      if (!isValidSenegalPhone(normalizedPhone)) {
        throw new Error('Numéro de téléphone sénégalais invalide');
      }
      if (!isValidSecretCode(secretCode)) {
        throw new Error('Le code secret doit contenir 4 à 6 chiffres');
      }
      if (!otpCode || String(otpCode).trim().length < 4) {
        throw new Error('Code SMS requis');
      }

      const response = await api.post('/auth/register', {
        phone: normalizedPhone,
        secretCode,
        firstName,
        lastName,
        otpCode: String(otpCode).trim(),
        role: 'CLIENT',
      });

      persistSession(response.data);

      return {
        success: true,
        data: response.data,
        mustSetSecretCode: false,
      };
    } catch (error) {
      console.error('Erreur inscription:', error);
      const networkMessage = !error.response
        ? (error.code === 'ECONNABORTED'
          ? 'Le serveur met trop de temps à répondre.'
          : 'Impossible de joindre le serveur. Vérifiez votre connexion.')
        : null;
      return {
        success: false,
        message: error.response?.data?.message || networkMessage || error.message || 'Erreur lors de l\'inscription',
        code: error.response?.data?.code,
      };
    }
  },

  async sendOtp(phone, purpose = 'register') {
    try {
      const normalizedPhone = normalizePhone(phone);
      if (!isValidSenegalPhone(normalizedPhone)) {
        return { success: false, message: 'Numéro sénégalais invalide' };
      }
      const response = await api.post('/auth/otp/send', {
        phone: normalizedPhone,
        purpose,
      });
      const data = response.data || {};
      if (data.simulated || data.devCode) {
        return {
          success: false,
          message: 'Le SMS n\'a pas pu être envoyé. Réessayez plus tard.',
        };
      }
      return { success: true, ...data };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Impossible d\'envoyer le SMS',
        code: error.response?.data?.code,
        waitSec: error.response?.data?.waitSec,
      };
    }
  },

  async verifyOtp(phone, code, purpose = 'register', { consume = true } = {}) {
    try {
      const normalizedPhone = normalizePhone(phone);
      if (!isValidSenegalPhone(normalizedPhone)) {
        return { success: false, message: 'Numéro sénégalais invalide' };
      }
      const response = await api.post('/auth/otp/verify', {
        phone: normalizedPhone,
        code,
        purpose,
        consume,
      });
      return { success: true, ...response.data };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Code SMS invalide',
        code: error.response?.data?.code,
      };
    }
  },

  async login(phone, credential, usesSecretCode = true) {
    try {
      const normalizedPhone = normalizePhone(phone);
      if (!isValidSenegalPhone(normalizedPhone)) {
        throw new Error('Numéro de téléphone sénégalais invalide');
      }

      const payload = { phone: normalizedPhone };
      if (usesSecretCode) {
        payload.secretCode = credential;
      } else {
        payload.password = credential;
      }

      const response = await api.post('/auth/login', payload);
      persistSession(response.data);

      return {
        success: true,
        data: response.data,
        mustSetSecretCode: !!response.data.mustSetSecretCode,
      };
    } catch (error) {
      console.error('Erreur connexion:', error);

      if (error.response?.status === 403 && error.response?.data?.code === 'ACCOUNT_DEACTIVATED') {
        return {
          success: false,
          message: 'Compte désactivé',
          code: 'ACCOUNT_DEACTIVATED',
        };
      }

      return {
        success: false,
        message: error.response?.data?.message || 'Erreur lors de la connexion',
      };
    }
  },

  async setupSecretCode(secretCode, confirmSecretCode) {
    try {
      const response = await api.post('/auth/setup-secret-code', {
        secretCode,
        confirmSecretCode,
      });
      if (response.data.user) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Erreur lors de la configuration du code',
      };
    }
  },

  logout() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },

  async getProfile() {
    try {
      const response = await api.get('/auth/profile');
      localStorage.setItem('user', JSON.stringify(response.data.user));
      return { success: true, data: response.data.user };
    } catch (error) {
      console.error('Erreur récupération profil:', error);
      return { success: false, message: 'Erreur lors de la récupération du profil' };
    }
  },

  isAuthenticated() {
    return !!localStorage.getItem('token');
  },

  getCurrentUser() {
    const userStr = localStorage.getItem('user');
    if (!userStr) return null;
    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  },
};

export default authService;
